/**
 * BaseModal Component
 * Base modal component that handles overlay, blur, and animation logic
 * Provides consistent backdrop behavior across all modals
 */

import React from 'react';
import {
  Modal,
  Pressable,
  View,
  Platform,
  StyleSheet,
  type ViewStyle,
} from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { BlurView } from 'expo-blur';
import {
  baseModalStyles as styles,
  getBackdropContainerStyle,
  getBlurContainerStyle,
  getContentContainerStyle,
} from './BaseModal.styles';
import { MODAL_SCRIM_ALPHA } from '../../../src/theme/modalOverlay.constants';
import { useSettledModalVisibility } from '../../../src/shared/hooks/useSettledModalVisibility';

export interface BaseModalProps {
  /** Whether the modal is visible */
  isVisible: boolean;
  /** Callback when modal should be dismissed */
  onDismiss: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Whether backdrop press should dismiss the modal */
  dismissible?: boolean;
  /** Custom backdrop opacity (0-1) */
  backdropOpacity?: number;
  /** Blur intensity (0-100, only on native) */
  blurIntensity?: number;
  /** Custom backdrop style */
  backdropStyle?: ViewStyle;
  /** Custom content container style */
  contentContainerStyle?: ViewStyle;
  /** Content alignment */
  contentAlignment?: 'center' | 'flex-start' | 'flex-end';
  /** Content position (for side menus, etc.) */
  contentPosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  /** Test ID for testing */
  testID?: string;
  /** Test ID for the content wrapper (portal content; used by Storybook play). */
  contentTestID?: string;
  /** Accessible name announced for the modal content (A11Y-3). */
  accessibilityLabel?: string;
  /** RN Modal wrapper animation; `none` avoids fade timing (e.g. Storybook). */
  animationType?: 'none' | 'fade' | 'slide';
  /** When true, omit web CSS transition on backdrop (e.g. Storybook). */
  disableWebBackdropTransition?: boolean;
  /**
   * When false, the overlay does not capture touches outside modal content (e.g. slide-out nav).
   * Backdrop is fully transparent; use for drawers where the page stays interactive.
   */
  blocksBackgroundInteraction?: boolean;
  /**
   * Native only: draw the backdrop and content as a full-screen layer inside the app instead of a
   * native `<Modal>`. For blocking prompts that must appear however they were reached — iOS refuses a
   * Modal presented while any other screen (sign-in sheet, account switcher, another Modal) is still
   * closing (specs/coin-compliance AC-5.5). Render it after the content it covers, in a full-screen parent.
   */
  renderInline?: boolean;
}

/**
 * BaseModal Component
 * Handles transparent backdrop with fade animation, touch-to-dismiss overlay,
 * and platform-specific blur handling (BlurView on native, dark backdrop on web).
 * When `blocksBackgroundInteraction` is false on web, content is rendered via a body
 * portal so pointer events reach the page outside the drawer (RN Web Modal is fullscreen).
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  isVisible,
  onDismiss,
  children,
  dismissible = true,
  backdropOpacity = MODAL_SCRIM_ALPHA,
  blurIntensity = 20,
  backdropStyle,
  contentContainerStyle,
  contentAlignment = 'center',
  contentPosition,
  testID,
  contentTestID,
  accessibilityLabel,
  animationType = 'fade',
  disableWebBackdropTransition = false,
  blocksBackgroundInteraction = true,
  renderInline = false,
}) => {
  const isInline = renderInline && Platform.OS !== 'web';
  // iOS: never present mid-transition (specs/coin-compliance Story 5). An inline layer is not a
  // native presentation, so it neither waits nor makes others wait.
  const nativeVisible = useSettledModalVisibility(isInline ? false : isVisible);

  const handleBackdropPress = () => {
    if (dismissible) {
      onDismiss();
    }
  };

  const effectiveBackdropAlpha = blocksBackgroundInteraction ? backdropOpacity : 0;
  const backdropColor = `rgba(0, 0, 0, ${effectiveBackdropAlpha})`;

  const containerStyle = getBackdropContainerStyle(
    contentAlignment,
    contentPosition,
    backdropColor,
    backdropStyle
  );
  const contentStyle = getContentContainerStyle(contentPosition, contentContainerStyle);

  const contentNode = (
    <View
      style={contentStyle}
      pointerEvents="box-none"
      testID={contentTestID}
      accessibilityViewIsModal={blocksBackgroundInteraction}
      accessibilityLabel={accessibilityLabel}
    >
      {children}
    </View>
  );

  const modalLayer = (
    <View
      style={[styles.backdrop, containerStyle]}
      pointerEvents={blocksBackgroundInteraction ? 'auto' : 'box-none'}
      testID={testID || 'base-modal'}
    >
      {blocksBackgroundInteraction ? (
        <Pressable
          style={[
            styles.backdropTouchable,
            !disableWebBackdropTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          ]}
          onPress={handleBackdropPress}
          testID={testID ? `${testID}-backdrop` : 'base-modal-backdrop'}
        />
      ) : null}
      {Platform.OS === 'web' ? null : (
        <BlurView
          pointerEvents="none"
          intensity={blurIntensity}
          style={[StyleSheet.absoluteFillObject, getBlurContainerStyle(contentAlignment, contentPosition)]}
        />
      )}
      {contentNode}
    </View>
  );

  if (isInline) {
    return isVisible ? (
      <View style={styles.inlineLayer} pointerEvents="box-none">
        {modalLayer}
      </View>
    ) : null;
  }

  const useWebNonBlockingPortal =
    Platform.OS === 'web' && !blocksBackgroundInteraction;

  if (useWebNonBlockingPortal) {
    if (!isVisible || typeof document === 'undefined') {
      return null;
    }
    const ReactDOM = require('react-dom') as typeof import('react-dom');
    return ReactDOM.createPortal(modalLayer, document.body);
  }

  return (
    <Modal
      visible={nativeVisible}
      transparent
      animationType={animationType}
      onRequestClose={() => onDismiss()}
      statusBarTranslucent
    >
      {modalLayer}
    </Modal>
  );
};

